/**
 * Migration helper hook
 * Provides backwards-compatible interface while using new architecture
 * This allows gradual migration of ResourceBoard.js
 */
import { useCallback, useEffect } from 'react';
import { useResourceBoard } from '../context/ResourceBoardContext';
import resourceBoardService from '../services/resourceBoardService';
import logger from '../utils/logger';

/**
 * Hook that provides old-style state management with new architecture underneath
 * Usage: Replace individual useState calls with this hook
 */
export function useResourceBoardMigration() {
  const { state, actions, dispatch } = useResourceBoard();

  // Backwards compatible API
  return {
    // State (read-only, matches old useState names)
    loading: state.loading,
    projects: state.projects,
    users: state.users,
    assignments: state.assignments,
    cars: state.cars,
    carAssignments: state.carAssignments,
    travelData: state.travelData,
    carsData: state.cars, // Alias for backward compatibility
    notes: state.notes,
    expandedProjects: state.expandedProjects,
    selectedTimeRange: state.selectedTimeRange,
    currentDate: state.currentDate,
    filters: state.filters,

    // Setters (backwards compatible with old setState)
    setLoading: actions.setLoading,
    setProjects: actions.setProjects,
    setUsers: actions.setUsers,
    setAssignments: actions.setAssignments,
    setCars: (carsOrUpdater) => {
      if (typeof carsOrUpdater === 'function') {
        const updatedCars = carsOrUpdater(state.cars);
        dispatch({ type: 'SET_CARS', payload: updatedCars });
      } else {
        dispatch({ type: 'SET_CARS', payload: carsOrUpdater });
      }
    },
    setCarsData: (carsOrUpdater) => {
      if (typeof carsOrUpdater === 'function') {
        const updatedCars = carsOrUpdater(state.cars);
        dispatch({ type: 'SET_CARS', payload: updatedCars });
      } else {
        dispatch({ type: 'SET_CARS', payload: carsOrUpdater });
      }
    },
    setCarAssignments: (carAssignmentsOrUpdater) => {
      if (typeof carAssignmentsOrUpdater === 'function') {
        const updatedCarAssignments = carAssignmentsOrUpdater(state.carAssignments);
        dispatch({ type: 'SET_CAR_ASSIGNMENTS', payload: updatedCarAssignments });
      } else {
        dispatch({ type: 'SET_CAR_ASSIGNMENTS', payload: carAssignmentsOrUpdater });
      }
    },
    setTravelData: (travelData) => dispatch({ type: 'SET_TRAVEL_DATA', payload: travelData }),
    setNotes: (notes) => dispatch({ type: 'SET_NOTES', payload: notes }),
    setExpandedProjects: (expanded) => dispatch({ type: 'SET_EXPANDED_PROJECTS', payload: expanded }),
    setSelectedTimeRange: (range) => dispatch({ type: 'SET_SELECTED_TIME_RANGE', payload: range }),
    setCurrentDate: (date) => dispatch({ type: 'SET_CURRENT_DATE', payload: date }),
    setFilters: actions.setFilters,

    // Service layer wrappers (replaces direct axios calls)
    api: {
      // Assignments
      getAssignments: (filters) => resourceBoardService.getAssignments(filters),
      createAssignment: (data) => resourceBoardService.createAssignment(data),
      updateAssignment: (id, data) => resourceBoardService.updateAssignment(id, data),
      deleteAssignment: (id) => resourceBoardService.deleteAssignment(id),

      // Projects
      getProjects: (filters) => resourceBoardService.getProjects(filters),

      // Users
      getUsers: (filters) => resourceBoardService.getUsers(filters),

      // Cars
      getCars: (filters) => resourceBoardService.getCars(filters),
      getCarAssignments: (date) => resourceBoardService.getCarAssignments(date),
      createCarAssignment: (data) => resourceBoardService.createCarAssignment(data),
      updateCarAssignment: (id, data) => resourceBoardService.updateCarAssignment(id, data),
      deleteCarAssignment: (id) => resourceBoardService.deleteCarAssignment(id),

      // Travel
      getTravelData: (filters) => resourceBoardService.getTravelData(filters),
      createTravel: (data) => resourceBoardService.createTravel(data),
      updateTravel: (id, data) => resourceBoardService.updateTravel(id, data),
      deleteTravel: (id) => resourceBoardService.deleteTravel(id),

      // Notes
      getNotes: (filters) => resourceBoardService.getNotes(filters),
      createNote: (data) => resourceBoardService.createNote(data),
      updateNote: (id, data) => resourceBoardService.updateNote(id, data),
      deleteNote: (id) => resourceBoardService.deleteNote(id),

      // Batch operations
      batchCreateAssignments: (assignments) => resourceBoardService.batchCreateAssignments(assignments),
      batchUpdateAssignments: (updates) => resourceBoardService.batchUpdateAssignments(updates),
      batchDeleteAssignments: (ids) => resourceBoardService.batchDeleteAssignments(ids),

      // Holidays
      getHolidays: (filters) => resourceBoardService.getHolidays(filters),
      createHoliday: (data) => resourceBoardService.createHoliday(data),
      updateHoliday: (id, data) => resourceBoardService.updateHoliday(id, data),
      deleteHoliday: (id) => resourceBoardService.deleteHoliday(id),
      approveHoliday: (id) => resourceBoardService.approveHoliday(id),
      rejectHoliday: (id, reason) => resourceBoardService.rejectHoliday(id, reason)
    },

    // Logger (replaces console.log)
    log: {
      debug: (...args) => logger.debug(...args),
      info: (...args) => logger.info(...args),
      warn: (...args) => logger.warn(...args),
      error: (...args) => logger.error(...args),
      group: (label, callback) => logger.group(label, callback),
      time: (label) => logger.time(label),
      timeEnd: (label) => logger.timeEnd(label)
    },

    // Original context access (for advanced use)
    _context: { state, actions, dispatch }
  };
}

/**
 * Hook for data fetching (replaces useEffect + axios pattern)
 */
export function useResourceBoardDataFetch() {
  const { api, setProjects, setUsers, setAssignments, setCars, setTravelData, setLoading, log } = useResourceBoardMigration();

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      log.time('Data fetch');

      // Fetch in parallel
      const [projects, users, assignments, cars, travelData] = await Promise.all([
        api.getProjects(),
        api.getUsers(),
        api.getAssignments(),
        api.getCars(),
        api.getTravelData()
      ]);

      log.timeEnd('Data fetch');

      // Update state
      setProjects(projects);
      setUsers(users);
      setAssignments(assignments);
      setCars(cars);
      setTravelData(travelData);

      setLoading(false);
      log.info('All data loaded successfully');

      return { projects, users, assignments, cars, travelData };
    } catch (error) {
      log.error('Error fetching data:', error);
      setLoading(false);
      throw error;
    }
  }, [api, setProjects, setUsers, setAssignments, setCars, setTravelData, setLoading, log]);

  return {
    fetchAllData,
    refreshAssignments: async () => {
      const assignments = await api.getAssignments();
      setAssignments(assignments);
      return assignments;
    },
    refreshProjects: async () => {
      const projects = await api.getProjects();
      setProjects(projects);
      return projects;
    }
  };
}

export default useResourceBoardMigration;
